// ---------------------------------------------------------------------------
// InputGroupCardV3 — card de agrupamento de input boxes (V3).
// ---------------------------------------------------------------------------
// Extraido do monolito ``InputsV3.js`` (leva 57-K). Preserva o visual
// anterior (Card + header cinza com borda inferior do cliente + grid
// de InputBoxV3) e adiciona comportamento de **accordion**:
//
//   - Header clicavel alterna entre "aberto" e "fechado".
//   - Quando fechado, os input boxes NAO desaparecem — recebem a
//     prop ``compact={true}`` do ``InputBoxV3``, que colapsa donut +
//     linha de responsavel mas preserva nome (single-line com
//     ellipsis) e os 3 "traços" de progresso. A transicao acontece
//     no proprio elemento (sem unmount/remount).
//   - O estado de "qual grupo esta aberto" e controlado pelo pai
//     (``InputGroupListV3``) via ``isOpen`` + ``onToggle``, para
//     garantir que abrir um grupo feche os demais.
//
// Notas operacionais:
//   - O chevron no header usa rotacao CSS — sem swap de icone.
//   - A animacao de slide-up/slide-down se da pelo proprio
//     ``InputBoxV3`` (que colapsa internamente): como eles
//     permanecem no DOM, o layout "respira" de forma suave.
//   - Nao usamos ``Collapse`` do MUI para o body porque queremos
//     que os cards permaneçam visiveis quando fechado (apenas
//     compactos).
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import ExpandMore from "@material-ui/icons/ExpandMore";

import Card from "../../../components/Card/Card.js";
import CardBody from "../../../components/Card/CardBody.js";
import { whiteColor } from "../../../assets/jss/material-dashboard-pro-react.js";
import InputBoxV3 from "./InputBoxV3";

// Paleta do header alinhada com o modal ``ProgressDashboardModal``:
// fundo cinza escuro, titulo branco alinhado a esquerda, strip
// vertical na cor do cliente. Carat de colapsar fica a direita.
const HEADER_BG = "#353534";

const headerStyle = {
  backgroundColor: HEADER_BG,
  color: whiteColor,
  margin: "0 -10px 10px -10px",
  borderRadius: "6px 6px 0 0",
  cursor: "pointer",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "6px 12px",
  transition: "background-color 0.15s ease",
};

const stripStyle = (clientColor) => ({
  width: 4,
  height: 20,
  borderRadius: 2,
  flexShrink: 0,
  background: clientColor,
});

const titleStyle = {
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: "-0.01em",
  color: whiteColor,
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const chevronStyle = (open) => ({
  fontSize: 20,
  transition: "transform 0.25s ease",
  transform: open ? "rotate(180deg)" : "rotate(0deg)",
  color: whiteColor,
  flexShrink: 0,
});

const InputGroupCardV3 = ({
  title,
  inputs,
  isOpen,
  onToggle,
  onInputClick,
  clientColor,
  displayResponsibleName,
  onInputFileDrop,
  onDropReject,
}) => {
  const compact = !isOpen;
  return (
    <Card
      style={{
        minWidth: "1200px",
        marginTop: "8px",
        marginBottom: "0px",
      }}
    >
      <CardBody
        style={{
          padding: "0 10px 3px 10px",
        }}
      >
        <div
          style={headerStyle}
          onClick={onToggle}
          role="button"
          aria-expanded={isOpen}
        >
          <div style={stripStyle(clientColor)} />
          <span style={titleStyle}>{title}</span>
          <ExpandMore style={chevronStyle(isOpen)} />
        </div>
        <div
          style={{
            marginTop: "8px",
            paddingBottom: "8px",
            display: "flex",
            flexWrap: "wrap",
            // Gap constante (12px) entre os cards, independente do
            // estado aberto/compacto do grupo. O gap variavel
            // (8px compacto / 12px aberto) amplificava a sensacao
            // de "largura do input box oscilando" quando o grupo
            // abria/fechava.
            gap: "12px",
            alignItems: "flex-start",
          }}
        >
          {inputs.map((input, idx) => (
            <InputBoxV3
              key={input.meta_name || idx}
              input={input}
              onClick={onInputClick}
              displayResponsible={displayResponsibleName}
              compact={compact}
              onInputFileDrop={onInputFileDrop}
              onDropReject={onDropReject}
            />
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

InputGroupCardV3.propTypes = {
  title: PropTypes.node.isRequired,
  inputs: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onInputClick: PropTypes.func,
  clientColor: PropTypes.string.isRequired,
  displayResponsibleName: PropTypes.string,
  onInputFileDrop: PropTypes.func,
  onDropReject: PropTypes.func,
};

export default InputGroupCardV3;
